import { useEffect, useState, useRef } from 'react';
import { Navigation, X, MapPin } from 'lucide-react';

interface NavigationCompassProps {
  targetLat: number;
  targetLng: number;
  targetName?: string;
  onClose?: () => void;
  className?: string;
}

// Calculate bearing from point A to point B in degrees
function calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;
  
  const dLng = toRad(lng2 - lng1);
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);
  
  const y = Math.sin(dLng) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - 
            Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
  
  let bearing = toDeg(Math.atan2(y, x));
  bearing = (bearing + 360) % 360;
  
  return bearing;
}

// Calculate distance between two points in meters
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth's radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c;
}

export default function NavigationCompass({ 
  targetLat, 
  targetLng, 
  targetName = 'Target',
  onClose,
  className 
}: NavigationCompassProps) {
  const [deviceHeading, setDeviceHeading] = useState<number>(0); // 0-360 degrees
  const [smoothHeading, setSmoothHeading] = useState<number>(0); // smoothed continuous heading
  const [, setCurrentPos] = useState<{lat: number; lng: number} | null>(null);
  const [bearing, setBearing] = useState<number>(0); // Direction to target
  const [distance, setDistance] = useState<number>(0);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  
  const watchIdRef = useRef<number | null>(null);
  const compassRef = useRef<HTMLDivElement>(null);

  // Start GPS tracking
  useEffect(() => {
    if (!navigator.geolocation) {
      setHasPermission(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setCurrentPos({ lat: latitude, lng: longitude });
        setAccuracy(accuracy);
        
        // Calculate bearing and distance to target
        const bearingToTarget = calculateBearing(latitude, longitude, targetLat, targetLng);
        const dist = calculateDistance(latitude, longitude, targetLat, targetLng);
        
        setBearing(bearingToTarget);
        setDistance(dist);
        setIsLoading(false);
      },
      (error) => {
        console.error('GPS error:', error);
        setHasPermission(false);
        setIsLoading(false);
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [targetLat, targetLng]);

  // Handle device orientation/compass
  useEffect(() => {
    const handleOrientation = (event: DeviceOrientationEvent) => {
      let heading = 0;
      
      // iOS: webkitCompassHeading
      const webkitEvent = event as any;
      if (webkitEvent.webkitCompassHeading !== undefined) {
        heading = webkitEvent.webkitCompassHeading;
      } 
      // Android: alpha with compensation
      else if (event.alpha !== null) {
        heading = 360 - event.alpha;
      }
      
      heading = (heading + 360) % 360;
      setDeviceHeading(heading);
      
      // Smooth rotation: avoid 360° jumps by tracking continuous rotation
      setSmoothHeading(prev => {
        const diff = heading - (prev % 360);
        // If the jump is large (>180°), we've crossed the 0/360 boundary
        if (diff > 180) return prev + diff - 360;
        if (diff < -180) return prev + diff + 360;
        return prev + diff;
      });
    };

    // Request permission for iOS 13+
    const requestPermission = async () => {
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        try {
          const permission = await (DeviceOrientationEvent as any).requestPermission();
          if (permission === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation, true);
          } else {
            setHasPermission(false);
          }
        } catch (e) {
          setHasPermission(false);
        }
      } else {
        // Android or older iOS - no permission needed
        window.addEventListener('deviceorientation', handleOrientation, true);
      }
    };

    requestPermission();

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, []);

  // Calculate relative bearing (direction to target relative to device heading)
  // Use smooth heading for continuous rotation without 360° jumps
  const relativeBearing = (() => {
    let relative = bearing - smoothHeading;
    // Normalize to 0-360 for display, but the rotation will be smooth
    relative = (relative + 360) % 360;
    return relative;
  })();
  
  // Compass ring rotation (negative because ring rotates opposite to device)
  const ringRotation = -smoothHeading;

  // Get cardinal direction text
  const getDirectionText = (degrees: number): string => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  // Format distance
  const formattedDistance = distance > 1000 
    ? `${(distance / 1000).toFixed(1)} km` 
    : `${Math.round(distance)} m`;

  if (!hasPermission) {
    return (
      <div className={`bg-card border border-border rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Navigation className="w-4 h-4" />
            Navigation
          </h3>
          {onClose && (
            <button onClick={onClose} className="p-1 rounded-full hover:bg-accent">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Please enable location and compass permissions to use navigation.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-xl p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Navigation className="w-4 h-4 text-primary" />
          Navigation
        </h3>
        <div className="flex items-center gap-2">
          {accuracy !== null && (
            <span className="text-[10px] text-muted-foreground">
              ±{Math.round(accuracy)}m
            </span>
          )}
          {onClose && (
            <button onClick={onClose} className="p-1 rounded-full hover:bg-accent">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Target Info */}
      <div className="mb-3 p-2 bg-accent/50 rounded-lg">
        <div className="flex items-center gap-2">
          <MapPin className="w-3 h-3 text-primary" />
          <span className="text-xs font-medium truncate">{targetName}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-lg font-bold text-primary">{formattedDistance}</span>
          <span className="text-xs text-muted-foreground">{getDirectionText(bearing)}</span>
        </div>
      </div>

      {/* Compass */}
      <div className="relative w-48 h-48 mx-auto">
        {/* Outer ring with cardinal directions - rotates with device heading */}
        <div 
          ref={compassRef}
          className="absolute inset-0 rounded-full border-4 border-border bg-background"
          style={{ 
            transform: `rotate(${ringRotation}deg)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          {/* Cardinal direction markers on the ring */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-red-500">N</div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-bold text-muted-foreground">S</div>
          <div className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">W</div>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">E</div>
          
          {/* Tick marks around the edge - 12 ticks at 30 degree intervals */}
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30 - 90) * (Math.PI / 180); // -90 to start at top
            const radius = 92; // Distance from center to edge
            const x = 50 + (radius / 192 * 100) * Math.cos(angle); // 192 is container width
            const y = 50 + (radius / 192 * 100) * Math.sin(angle);
            return (
              <div 
                key={i}
                className="absolute w-0.5 h-2 bg-muted-foreground/30"
                style={{ 
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: `translate(-50%, -50%) rotate(${i * 30}deg)`,
                }}
              />
            );
          })}
        </div>

        {/* Fixed direction arrow pointing to target - rotates to show direction */}
        <div 
          className="absolute inset-0 flex items-start justify-center pt-3"
          style={{
            transform: `rotate(${relativeBearing}deg)`,
            transition: 'transform 0.2s ease-out'
          }}
        >
          <Navigation 
            className="w-10 h-10 text-primary fill-primary/20 drop-shadow-lg" 
            strokeWidth={1.5}
            style={{ transform: 'rotate(-45deg)' }} // Navigation icon points NE, rotate to point up
          />
        </div>

        {/* Center dot */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full border-2 border-background shadow-lg" />
      </div>

      {/* Instructions */}
      <p className="text-[10px] text-center text-muted-foreground mt-3">
        Keep the arrow pointing up to navigate toward the incident
      </p>

      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-xl flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-muted-foreground">Getting GPS fix...</span>
          </div>
        </div>
      )}
    </div>
  );
}
